import './styles.sass';

import React from 'react';
import { camelCase } from 'lodash-es';

import Transition from 'components/Explorer/Transition';
import useExplorerData, { Loading } from 'hooks/useExplorerData';

import { mergeMentionCounts, mergeSentimentSummary } from './helpers';
import Fallback from './Fallback';
import HeadlineBar from './HeadlineBar';
import SummaryBarContent from './SummaryBarContent';
import { sentimentNames } from 'components/Explorer/util/sentiment_helpers';
import useQueryParams from 'hooks/useQueryParams';

const IGNORED_FILTER_KEYS = [
  // Global params we can ignore
  'scope',

  // Sub filters from the mentions tab
  'mention_sources[]',
  'countries[]',
  'mentioned_after',
  'mentioned_before',
  'sentiment[]',

  // Sub filters from the mention sources tab
  'mention_sources_countries[]',
  'mention_sources_mentioned_after',
  'mention_sources_mentioned_before',
  'mention_sources_types[]'
];

const emptySentimentSummary = () =>
  sentimentNames().reduce((acc, sentimentName) => {
    acc[camelCase(sentimentName)] = { sentimentName, count: 0, percentage: 0 };
    return acc;
  }, {});

const SentimentSummary = () => {
  const params = useQueryParams();

  const advancedSearchFilters = Object.keys(params.filters || {}).filter((key) => !IGNORED_FILTER_KEYS.includes(key));
  const searchParamsPresent = advancedSearchFilters.length > 0;

  const { data, loading } = useExplorerData('highlights/sentiments', params, {
    reloadOn: ['filters'],
    enabled: searchParamsPresent
  });

  const isLoading = loading === Loading.IN_PROGRESS || loading === Loading.NOT_STARTED;
  const didFail = loading === Loading.ERROR;

  const mentionCounts = data ? mergeMentionCounts({ withSentiment: 0 }, data.mention_counts) : { withSentiment: 0 };
  const sentimentSummary = data
    ? mergeSentimentSummary(emptySentimentSummary(), data.summary)
    : emptySentimentSummary();

  // If no real filters are applied, skip fetching entirely.
  const skip = !searchParamsPresent;
  const showSummary = !skip && !isLoading && !didFail && mentionCounts.withSentiment > 0 && searchParamsPresent;

  return (
    <div className="Highlights-Sentiment-Summary Highlights-block">
      <div className="Highlights-title">
        <HeadlineBar
          mentionCount={mentionCounts.withSentiment}
          onInfoClicked={() => Analytics.trackEvent('Sentiment Summary: Open-Popover-Button clicked', {})}
          onLinkClicked={() => Analytics.trackEvent('Sentiment Summary: Learn-More-Link clicked', {})}
          onCloseClicked={() => Analytics.trackEvent('Sentiment Summary: Close-Popover-Button clicked', {})}
          loading={!skip && isLoading}
        />
      </div>
      <div className="Highlights-content-wrapper">
        <Transition classNames="fade-in" timeout={250}>
          <div className="fade-in">
            {showSummary ? (
              <SummaryBarContent sentimentSummary={sentimentSummary} />
            ) : (
              <Fallback
                isLoading={!skip && isLoading}
                mentionCounts={mentionCounts}
                didFail={didFail}
                searchParamsPresent={searchParamsPresent}
              />
            )}
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default SentimentSummary;
